import * as React from 'react'
import { useEffect, useRef, useState } from 'react'

import { useOrdersList } from './hooks'
import { Order } from './types'
import { CButton, CCardBody } from '@coreui/react-pro'
import { Badge, InputRef, Select, Table, Tag } from 'antd'
import { path, role } from '../../../../libs'
import { ColumnsType } from 'antd/lib/table'
import { FilterValue } from 'antd/es/table/interface'
import { useStore } from 'effector-react'
import { $userRole } from '../../../../features/user'
import {
  getColumnSearchDateProps,
  getColumnSearchProps,
} from '../../tableFunctions'
import {
  backendInstallmentFilter,
  backendStatusFilter,
  convertOrderStatus,
  paymentMethod,
  statusFilter,
} from './consts'
import { renderOrderItems } from './orderItems'
import Title from 'antd/lib/typography/Title'
import dayjs from 'dayjs'

export const OrderList = () => {
  const { orders, totalCountOfOrders, loading, onChangeOrdersList } =
    useOrdersList()
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const [searchText, setSearchText] = useState<{
    [key: string]: string
  }>({})
  const [orderBy, setOrderBy] = useState('id')
  const [orderDirection, setOrderDirection] = useState('-')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [filtersState, setFiltersState] = useState<
    Record<string, FilterValue | null>
  >({})

  const userRole = useStore($userRole)

  const searchInput = useRef<InputRef>(null)

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
    filters,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
    filters?: Record<string, FilterValue | null>
  }) => {
    if (currentPage) {
      setPage(currentPage)
    }
    if (currentPageSize) {
      setPageSize(currentPageSize)
    }
    if (currentOrderBy) {
      setOrderBy(currentOrderBy)
    }
    if (currentOrderDirection) {
      if (currentOrderDirection == 'descend') {
        setOrderDirection('-')
      } else {
        setOrderDirection('')
      }
    }

    if (filters) {
      setFiltersState(filters)
    }
  }

  useEffect(() => {
    onChangeOrdersList({
      limit: pageSize,
      page: page,
      orderBy: orderBy,
      orderDirection: orderDirection,
      searchText: searchText,
      filters: filtersState,
    })
  }, [pageSize, page, orderBy, orderDirection, searchText, filtersState])

  const getCsvCode = () => {
    if (orders.length == 0) {
      return 'data:text/csv;charset=utf-8,'
    }
    const csvOrders = orders.map((item) => {
      return {
        ID: `${item.orderId}`,
        'Created at': `"${new Date(item.createdAt).toLocaleString()}"`,
        'User ID': `${item.userId}`,
        Status: `${convertOrderStatus(item.status)}`,
        'Backend status': `${convertOrderStatus(item.backendStatus)}`,
        Amount: `${item.amount} USD`,
        'Order details': `"${item.orderItems
          .map(
            (item) =>
              `Property Id: ${item.PropertyID}\nName: ${item.PropertyName}\nAmount: ${item.Amount}\nToken price: ${item.TokenPrice} USD\nPrice: ${item.Price} USD`,
          )
          .join('\n')}"`,
        Transaction: `${item.transactionRequestId}`,
      }
    })
    const header = Object.keys(csvOrders[0]).join(',')
    const data = csvOrders.map((item) => Object.values(item).join(','))
    const csvData = [header, ...data].join('\n') // [...header, ...data].join('\n')
    return 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvData)
  }

  const columns: ColumnsType<Order> = [
    {
      title: 'ID',
      dataIndex: 'orderId',
      ...getColumnSearchProps<Order>(
        'orderId',
        setSearchText,
        searchText,
        searchInput,
      ),
      sorter: (a, b) => parseInt(a.orderId) - parseInt(b.orderId),
      defaultSortOrder: 'descend',
    },
    {
      title: 'installment',
      dataIndex: 'isInstallment',
      filters: backendInstallmentFilter,
      filterMultiple: false,
      filterSearch: true,
      render: (text, record) => (
        <a
          target="_blank"
          href={path.nova.instalments.entry(record?.installmentId)}
        >
          {record?.installmentId}
        </a>
      ),
    },
    {
      title: 'Created at',
      dataIndex: 'createdAt',
      ...getColumnSearchDateProps<Order>(
        'createdAt',
        setSearchText,
        searchText,
        setIsCalendarOpen,
        isCalendarOpen,
        searchInput,
      ),
      render: (item) => <div>{new Date(item).toLocaleString()}</div>,
      sorter: true,
      width: '150px',
    },
    {
      title: 'User ID',
      ...getColumnSearchProps<Order>(
        'userId',
        setSearchText,
        searchText,
        searchInput,
      ),
      render: (item: Order) => (
        <div>
          {item.user?.isTest ? (
            <Badge count={'Test'} offset={[10, -10]}>
              <a
                href={path.clients.details(String(item.userId))}
                target="_blank"
              >
                {item.userId}
              </a>
            </Badge>
          ) : (
            <a href={path.clients.details(String(item.userId))} target="_blank">
              {item.userId}
            </a>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      filters: statusFilter,
      filterSearch: true,
      onFilter: (value: any, record) =>
        record.status.toString().includes(value),
      render: (item: string) => (
        <>
          <Tag
            color={
              convertOrderStatus(item) == 'completed' ? '#2eb85c' : '#9e9cad'
            }
          >
            {convertOrderStatus(item)}
          </Tag>
        </>
      ),
    },
    {
      title: 'Backend status',
      dataIndex: 'backendStatus',
      filters: backendStatusFilter,
      filterSearch: true,
      onFilter: (value: any, record) =>
        record.status.toString().includes(value),
      render: (item: string) => (
        <>
          <Tag
            color={
              convertOrderStatus(item) == 'completed' ? '#2eb85c' : '#9e9cad'
            }
          >
            {convertOrderStatus(item)}
          </Tag>
        </>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      render: (item) => <div>{item + ' USD'}</div>,
    },
    {
      title: 'Order details',
      dataIndex: 'orderItems',
      render: renderOrderItems,
    },
    {
      title: 'Payment method',
      dataIndex: 'gatewayName',
      ...getColumnSearchProps<Order>(
        'gatewayName',
        setSearchText,
        searchText,
        searchInput,
      ),
      render: (item: string) => (
        <>
          {item === paymentMethod.NOVA_WALLET ? (
            <Tag color="#50F9AC">{item} </Tag>
          ) : item === paymentMethod.AUTO_REDUCTION_ORDER ? (
            <Tag color="#9e9cad">{item} </Tag>
          ) : (
            item
          )}
        </>
      ),
    },
    {
      title: 'Transaction',
      dataIndex: 'transactionRequestId',
      ...getColumnSearchProps<Order>(
        'transactionRequestId',
        setSearchText,
        searchText,
        searchInput,
      ),
      sorter: (a, b) =>
        parseInt(a.transactionRequestId) - parseInt(b.transactionRequestId),
      render: (item) => (
        <a href={path.billing.deposit.edit(item)} target="_blank">
          {item}
        </a>
      ),
    },
  ]

  const handleChangeOrdersTypeSelect = (value: FilterValue) => {
    switch (value.toString()) {
      case 'users':
        setFiltersState((prevState) => ({
          ...prevState,
          gatewayId: ['>0'],
        }))
        break
      case 'auto':
        setFiltersState((prevState) => ({
          ...prevState,
          gatewayId: ['0'],
        }))
        break
      default:
        setFiltersState((prevState) => {
          delete prevState['gatewayId']
          return {
            ...prevState,
          }
        })
    }
  }

  const isDisplayDownloadButton =
    userRole?.filter((roleItem) =>
      [role.root(), role.head.cfo(), role.finances.financeManager()].includes(
        roleItem,
      ),
    ).length !== 0

  return (
    <CCardBody>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '20px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
          }}
        >
          {isDisplayDownloadButton && (
            <CButton
              color="primary"
              href={getCsvCode()}
              download={
                'orders ' +
                dayjs(Date.now()).format('YYYY-MM-DD HH-mm') +
                '.csv'
              }
              target="_blank"
            >
              Download current items (.csv)
            </CButton>
          )}
        </div>
        <div>
          <Title level={5}>Orders type</Title>
          <Select
            // @ts-ignore
            defaultValue=""
            style={{ width: 250 }}
            onChange={(value: FilterValue) =>
              handleChangeOrdersTypeSelect(value)
            }
            options={[
              { value: '', label: 'All' },
              { value: 'users', label: "User's orders" },
              {
                value: 'auto',
                label: 'Auto orders',
              },
            ]}
          />
        </div>
      </div>
      <Table
        dataSource={orders}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        onRow={(record, rowIndex) => {
          return {
            onClick: (event) => {},
          }
        }}
        rowKey="orderId"
        columns={columns}
        pagination={{
          showSizeChanger: true,
          total: totalCountOfOrders,
        }}
        onChange={(pagination, filters, sorter, extra) =>
          handleTableChange({
            //@ts-ignore
            currentPage: pagination.current, //@ts-ignore
            currentPageSize: pagination.pageSize, //@ts-ignore
            currentOrderDirection: sorter.order, //@ts-ignore
            currentOrderBy: sorter.field,
            filters: { ...filtersState, ...filters },
          })
        }
      />
    </CCardBody>
  )
}
